/**
 * @file api.ts
 * @brief TanStack Query hooks for a personal user's Billing area.
 *
 * @details
 * Same endpoints the phone's Billing / Plans / Saved Cards screens call. No
 * `company_id` on the card calls, so the backend resolves the caller's OWN
 * Stripe customer (billing/routes.js resolveCustomerId). Company admins keep
 * features/company-billing for the company customer.
 *
 * @author DES
 * @date 17-10-2026
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiClient, apiDelete, apiGet, apiPost } from "@/shared/api/client";
import { authEndpoints, billingEndpoints, boardroomEndpoints, invoiceEndpoints } from "@/shared/api/endpoints";
import {
  CardsResponseSchema,
  InvoicesResponseSchema,
  PlanCatalogSchema,
  PricingSchema,
  SearchQuotaSchema,
  SetupIntentSchema,
  SubscribeResponseSchema,
  SubscriptionStatusSchema,
  type CardsResponse,
  type InvoicesResponse,
  type PlanCatalog,
  type SubscriptionStatus,
} from "./schemas";

export const myBillingKeys = {
  all: ["my-billing"] as const,
  cards: () => ["my-billing", "cards"] as const,
  status: () => ["my-billing", "status"] as const,
  catalog: () => ["my-billing", "catalog"] as const,
  quota: () => ["my-billing", "quota"] as const,
  pricing: (kind: string) => ["my-billing", "pricing", kind] as const,
  invoices: (params: InvoiceListParams) => ["my-billing", "invoices", params] as const,
};

// ---------------------------------------------------------------- cards

export function useMyCards() {
  return useQuery<CardsResponse>({
    queryKey: myBillingKeys.cards(),
    queryFn: async () => CardsResponseSchema.parse(await apiGet<unknown>(billingEndpoints.customer)),
  });
}

export async function createMySetupIntent(): Promise<string> {
  const raw = await apiPost<unknown>(billingEndpoints.setupIntent, {});
  return SetupIntentSchema.parse(raw).clientSecret;
}

/** A SetupIntent for a card already saved here: "Confirm with your bank" (BACKEND f5e603e). */
export async function createMyConfirmIntent(paymentMethodId: string): Promise<string> {
  const raw = await apiPost<unknown>(billingEndpoints.setupIntent, { paymentMethodId });
  return SetupIntentSchema.parse(raw).clientSecret;
}

export async function setMyDefaultCard(paymentMethodId: string): Promise<void> {
  await apiPost<unknown>(billingEndpoints.paymentMethodDefault(paymentMethodId), {});
}

export function useSetMyDefaultCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: setMyDefaultCard,
    onSuccess: () => void qc.invalidateQueries({ queryKey: myBillingKeys.cards() }),
  });
}

/**
 * Detach a card, then make `promoteId` the default when the default went
 * (the backend leaves the customer with none; see cardRemoval.ts).
 */
export function useRemoveMyCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, promoteId }: { id: string; promoteId?: string | null }) => {
      const res = await apiDelete<unknown>(billingEndpoints.paymentMethodById(id));
      // Since BACKEND A4 (18-10-2026) the server promotes the next card itself
      // and says which (`newDefaultPaymentMethod`); only an older backend needs
      // the page to do it.
      const serverPromoted = !!res && typeof res === "object" && "newDefaultPaymentMethod" in res;
      if (promoteId && !serverPromoted) await setMyDefaultCard(promoteId);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: myBillingKeys.cards() }),
  });
}

// ---------------------------------------------------------------- plan

export function useMySubscription() {
  return useQuery<SubscriptionStatus>({
    queryKey: myBillingKeys.status(),
    queryFn: async () => SubscriptionStatusSchema.parse(await apiGet<unknown>(billingEndpoints.subscriptions)),
  });
}

/** The plan catalogue for Compare plans: no Stripe call, so cheap to read. */
export function usePlanCatalog() {
  return useQuery<PlanCatalog>({
    queryKey: myBillingKeys.catalog(),
    staleTime: 60_000,
    queryFn: async () => PlanCatalogSchema.parse(await apiGet<unknown>(billingEndpoints.subscriptionsCatalog)),
  });
}

export async function subscribeToPlan(input: {
  planCode: string;
  interval: "month" | "year";
  paymentMethodId: string;
}) {
  const raw = await apiPost<unknown>(billingEndpoints.subscriptions, input);
  return SubscribeResponseSchema.parse(raw);
}

/**
 * Free plan (B4, 22-10-2026): send the confirm-email link again. `sent:false`
 * = one went out in the last 10 minutes; `emailVerified:true` = already done,
 * so the status is re-read and the gate lifts.
 */
export function useResendVerifyEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      z
        .object({ sent: z.boolean().optional(), emailVerified: z.boolean().optional() })
        .passthrough()
        .parse(await apiPost<unknown>(authEndpoints.verifyEmail, {})),
    onSuccess: (data) => {
      if (data.emailVerified) void qc.invalidateQueries({ queryKey: myBillingKeys.status() });
    },
  });
}

/** Re-read the plan from Stripe through the server (it does not trust the page). */
export async function syncMySubscription(subscriptionId: string): Promise<SubscriptionStatus> {
  const raw = await apiPost<unknown>(billingEndpoints.subscriptionsSync, { subscriptionId });
  return SubscriptionStatusSchema.parse(raw);
}

export function useCancelMySubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      SubscriptionStatusSchema.parse(
        await apiPost<unknown>(billingEndpoints.subscriptionsCancel, { atPeriodEnd: true }),
      ),
    onSuccess: (data) => qc.setQueryData(myBillingKeys.status(), data),
  });
}

/** Switch plan or interval; `clientSecret` when the proration payment needs 3DS. */
export async function changeMyPlan(input: { planCode: string; interval: "month" | "year" }) {
  return SubscriptionStatusSchema.parse(await apiPost<unknown>(billingEndpoints.subscriptionsChange, input));
}

const ChangePreviewSchema = z.object({
  amountDueCents: z.number(),
  creditCents: z.number().nullable().optional(),
  currency: z.string(),
});
export type ChangePreview = z.infer<typeof ChangePreviewSchema>;

/** What a change costs now (or credits), before confirming. Null = not available. */
export function useChangePreview(target: { planCode: string; interval: "month" | "year" } | null) {
  return useQuery<ChangePreview | null>({
    queryKey: ["my-billing", "change-preview", target?.planCode, target?.interval],
    enabled: !!target,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      try {
        const usp = new URLSearchParams({ planCode: target!.planCode, interval: target!.interval });
        return ChangePreviewSchema.parse(
          await apiGet<unknown>(`${billingEndpoints.subscriptionsChangePreview}?${usp}`),
        );
      } catch {
        return null; // older backend or refused: the sheet states the rule instead
      }
    },
  });
}

/** Undo a cancel before the period ends. */
export function useResumeMySubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      SubscriptionStatusSchema.parse(await apiPost<unknown>(billingEndpoints.subscriptionsResume, {})),
    onSuccess: (data) => qc.setQueryData(myBillingKeys.status(), data),
  });
}

// ---------------------------------------------------------------- usage

export function useMyAskQuota() {
  return useQuery({
    queryKey: myBillingKeys.quota(),
    queryFn: async () => SearchQuotaSchema.parse(await apiGet<unknown>(boardroomEndpoints.searchQuota)),
  });
}

/** Pay-as-you-go list rate for one product line (`standard` | `realtime`). */
export function useMyPricing(kind: "standard" | "realtime") {
  return useQuery({
    queryKey: myBillingKeys.pricing(kind),
    queryFn: async () =>
      PricingSchema.parse(await apiGet<unknown>(`${billingEndpoints.pricing}?kind=${kind}`)),
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------- invoices

export type InvoiceListParams = { page?: number; pageSize?: number; from?: string; to?: string };

export function useMyInvoices(params: InvoiceListParams) {
  return useQuery<InvoicesResponse>({
    queryKey: myBillingKeys.invoices(params),
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const usp = new URLSearchParams();
      usp.set("page", String(params.page ?? 1));
      usp.set("pageSize", String(params.pageSize ?? 20));
      if (params.from) usp.set("from", params.from);
      if (params.to) usp.set("to", params.to);
      return InvoicesResponseSchema.parse(await apiGet<unknown>(`${invoiceEndpoints.list}?${usp}`));
    },
  });
}

/** The PDF through the authenticated client (the route needs the Bearer token). */
export async function downloadInvoicePdf(id: string): Promise<void> {
  const res = await apiClient.get(`${invoiceEndpoints.byId(id)}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `invoice-${id}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
